import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';

function formatChargeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(41, 41, 41, 0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: linear-gradient(
    0deg,
    #fff 94%,
    rgba(255, 237, 234, 1) 98%,
    rgba(245, 186, 176, 1) 100%
  );
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;

  @media (min-width: 768px) {
    border-radius: 16px;
  }
`;

const SheetContent = styled.div`
  padding: 32px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: #292929;
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #292929;
  margin: 0;
  text-align: center;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e5e5;
  margin: 0;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: #292929;
  border: none;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelTextButton = styled.button`
  background: none;
  border: none;
  padding: 10px 32px;
  font-size: 16px;
  font-weight: 400;
  color: #292929;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface SkipConfirmationSheetProps {
  readonly subscription: CustomerSubscription;
  readonly isLoading: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function SkipConfirmationSheet({
  subscription,
  isLoading,
  onConfirm,
  onCancel,
}: SkipConfirmationSheetProps) {
  const formattedDate = formatChargeDate(subscription.next_charge_date);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  return (
    <Backdrop onClick={handleBackdropClick} data-testid="skip-backdrop">
      <Sheet
        role="dialog"
        aria-modal="true"
        aria-label="Skip subscription confirmation"
      >
        <SheetContent>
          <Title>Are you sure you want to skip this subscription?</Title>

          <Subtitle>
            If you skip this subscription, you won&apos;t receive it in your
            scheduled order on {formattedDate}
          </Subtitle>

          <Divider />

          <CTASection>
            <ConfirmButton
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              data-testid="skip-confirm-button"
            >
              {isLoading ? 'Skipping...' : 'Yes, skip this order'}
            </ConfirmButton>
            <CancelTextButton
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              data-testid="skip-cancel-button"
            >
              Cancel
            </CancelTextButton>
          </CTASection>
        </SheetContent>
      </Sheet>
    </Backdrop>
  );
}
